import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Switch } from "../components/ui/switch";

export default function AccessManagement() {
    const qc = useQueryClient();
    const { data: features = [] } = useQuery({ queryKey: ["access-features"], queryFn: async () => (await api.get("/access/features")).data });
    const { data: users = [], isLoading } = useQuery({ queryKey: ["access-users"], queryFn: async () => (await api.get("/access/users")).data });

    const editable = features.filter((f) => !["gestao_acessos", "gestores"].includes(f.key));

    const toggle = async (user, featureKey, checked) => {
        const current = new Set(user.permissions || []);
        if (checked) current.add(featureKey); else current.delete(featureKey);
        try {
            await api.put(`/access/users/${user.user_id}`, { permissions: Array.from(current) });
            qc.invalidateQueries({ queryKey: ["access-users"] });
            toast.success(`Acesso de ${user.name} atualizado`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div>
            <PageHeader
                overline="Controle de acesso"
                title="Gestão de Acessos"
                description="Defina quais funções cada perfil pode acessar. O Master Geral tem acesso total e não pode ser alterado."
            />

            <div className="mb-6 flex items-center gap-3 rounded-2xl border border-accent/30 bg-accent/5 p-4 text-sm text-foreground shadow-[0_0_15px_rgba(140,168,153,0.15)]">
                <ShieldCheck className="h-5 w-5 shrink-0 text-accent" />
                Marque ou desmarque uma função para conceder ou remover o acesso de cada usuário. As mudanças valem no próximo carregamento de página do usuário.
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-border bg-white" data-testid="access-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                                <th className="px-5 py-4">Usuário</th>
                                {editable.map((f) => <th key={f.key} className="px-4 py-4 text-center">{f.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.user_id} className="border-b border-border/50 last:border-0 hover:bg-muted/30" data-testid={`access-row-${u.user_id}`}>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`flex h-9 w-9 items-center justify-center rounded-xl text-xs font-semibold ${u.is_master ? "bg-secondary text-secondary-foreground" : "bg-accent/15 text-accent"}`}>
                                                {u.is_master ? <Crown className="h-4 w-4" /> : u.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="font-medium">{u.name}</p>
                                                <p className="text-xs text-muted-foreground">{u.is_master ? "Master Geral" : `${u.role} · ${u.nivel}`}</p>
                                            </div>
                                        </div>
                                    </td>
                                    {editable.map((f) => (
                                        <td key={f.key} className="px-4 py-4 text-center">
                                            <div className="flex justify-center">
                                                <Switch
                                                    data-testid={`access-toggle-${u.user_id}-${f.key}`}
                                                    checked={u.is_master || (u.permissions || []).includes(f.key)}
                                                    disabled={u.is_master}
                                                    onCheckedChange={(c) => toggle(u, f.key, c)}
                                                />
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
